import { MAX_SCREENSHOT_BYTES } from "./feedbackForm";
import { settleWithin } from "./pageCaptureRules";

// A capture that hasn't finished by then is given up (the dialog shows the failure note).
export const CAPTURE_TIMEOUT_MS = 15_000;

// Overlays rendered inside the app (the mobile drawer is a <dialog>, the sidebar's account menu). The
// feedback dialog, command palette, toasts and tooltips portal to <body>, outside #root, so they're
// never in the capture.
const OVERLAYS = 'dialog, [role="dialog"], [role="alertdialog"], [aria-modal="true"], [role="tooltip"], [role="menu"]';

/** False for the nodes the page screenshot leaves out: overlays rather than the page itself. */
export function keepInCapture(node: Node): boolean {
  return !(node.nodeType === Node.ELEMENT_NODE && (node as Element).matches(OVERLAYS));
}

export interface Encoding {
  type: string;
  quality?: number;
  /** Relative to the rendered canvas. */
  scale: number;
}

/** Tried in order until one fits MAX_SCREENSHOT_BYTES: PNG, then lossy, then lossy at half size. */
export const ENCODINGS: readonly Encoding[] = [
  { type: "image/png", scale: 1 },
  { type: "image/webp", quality: 0.9, scale: 1 },
  { type: "image/jpeg", quality: 0.85, scale: 0.5 },
];

/** The first encoding that fits `limit`, or null if none does (the capture is then not attached). */
export async function encodeWithinLimit(
  encode: (encoding: Encoding) => Promise<Blob | null>,
  limit = MAX_SCREENSHOT_BYTES,
): Promise<Blob | null> {
  for (const encoding of ENCODINGS) {
    const blob = await encode(encoding);
    if (blob && blob.size > 0 && blob.size <= limit) return blob;
  }
  return null;
}

const EXTENSIONS: Record<string, string> = { "image/png": "png", "image/webp": "webp", "image/jpeg": "jpg" };

/** `page-screenshot.<ext>` for the encoded type (a browser may fall back to PNG for WebP). */
export const pageScreenshotName = (type: string) => `page-screenshot.${EXTENSIONS[type] ?? "png"}`;

function encodeCanvas(canvas: HTMLCanvasElement, { type, quality, scale }: Encoding): Promise<Blob | null> {
  let source = canvas;
  if (scale !== 1) {
    source = document.createElement("canvas");
    source.width = Math.max(1, Math.round(canvas.width * scale));
    source.height = Math.max(1, Math.round(canvas.height * scale));
    source.getContext("2d")?.drawImage(canvas, 0, 0, source.width, source.height);
  }
  return new Promise((resolve) => source.toBlob(resolve, type, quality));
}

/**
 * The library fetches each image to inline it, but CSP's connect-src doesn't allow blob: (previews
 * of uploads). Those are already loaded, so draw them instead; false lets the library fetch the rest.
 */
async function blobImageAsDataUrl(url: string): Promise<string | false> {
  if (!url.startsWith("blob:")) return false;
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext("2d")?.drawImage(image, 0, 0);
    return canvas.toDataURL("image/png");
  } catch {
    return false;
  }
}

/** The clone draws scrollbars on every scroll area; most people's (overlay) scrollbars don't show. */
function hideScrollbars(cloned: Node) {
  if (cloned.nodeType === Node.ELEMENT_NODE) (cloned as HTMLElement).style.scrollbarWidth = "none";
}

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Render the page (the app root, without overlays) to an image file, or null if it doesn't fit the
 * screenshot size limit. Rejects if rendering fails. modern-screenshot is loaded on first use.
 */
export async function capturePage(): Promise<File | null> {
  // Let whatever opened the dialog (the phone's menu drawer, the command palette) close first.
  await nextFrame();
  // Just after a page loads, its web fonts can still be loading, so the page is laid out in the
  // fallback font. The clone keeps those widths but draws the embedded design-system font, so text
  // re-wraps and overlaps. Wait until they've loaded (or failed) and the page has its real layout.
  await document.fonts?.ready;
  const { domToCanvas } = await import("modern-screenshot");
  const root = document.getElementById("root") ?? document.body;
  const options = {
    scale: Math.min(globalThis.devicePixelRatio ?? 1, 2),
    backgroundColor: getComputedStyle(document.body).backgroundColor,
    filter: keepInCapture,
    fetchFn: blobImageAsDataUrl,
    features: { restoreScrollPosition: true },
    onCloneEachNode: hideScrollbars,
  };
  let canvas: HTMLCanvasElement;
  try {
    canvas = await domToCanvas(root, options);
  } catch {
    // Embedding the design-system fonts failed (offline, blocked): a capture in fallback fonts beats none.
    canvas = await domToCanvas(root, { ...options, font: false });
  }
  const blob = await encodeWithinLimit((encoding) => encodeCanvas(canvas, encoding));
  return blob ? new File([blob], pageScreenshotName(blob.type), { type: blob.type }) : null;
}

/** Start capturing the page as the feedback dialog opens. Never rejects: null means no capture. */
export function startPageCapture(): Promise<File | null> {
  return settleWithin(capturePage(), CAPTURE_TIMEOUT_MS).then(
    (file) => file ?? null,
    () => null,
  );
}
